import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Shield, FileText, List, Lock, Settings, Download, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

const trustNav = [
  { label: "Trust Center", href: "/trust", icon: Shield, desc: "Overview" },
  { label: "Data Processing Addendum", href: "/dpa", icon: FileText, desc: "DPA" },
  { label: "Sub-Processors", href: "/subprocessors", icon: List, desc: "Vendors" },
  { label: "Privacy Policy", href: "/privacy", icon: Lock, desc: "User policy" },
  { label: "Cookie Policy", href: "/cookie-policy", icon: Settings, desc: "Cookies" },
];

function VersionHistory({ history }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-8 rounded-xl border overflow-hidden print:hidden" style={{ borderColor: '#E2EBE9' }}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="version-history-toggle"
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2.5">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#194449' }}>Version History</span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#194449' }}>
            v{history[0].version}
          </span>
        </div>
        {open
          ? <ChevronDown size={14} className="text-gray-400" />
          : <ChevronRight size={14} className="text-gray-400" />
        }
      </button>
      {open && (
        <div className="border-t bg-white px-5 py-4" style={{ borderColor: '#E2EBE9' }}>
          <div className="space-y-3.5">
            {history.map((h, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-14 text-xs font-bold shrink-0 mt-0.5" style={{ color: '#194449' }}>v{h.version}</div>
                <div>
                  <div className="text-xs font-semibold text-gray-800">{h.date}</div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">{h.notes}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const Section = ({ id, title, children, numbered }) => (
  <div id={id} className="mb-10 scroll-mt-32">
    {title && (
      <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b flex items-center gap-2"
        style={{ fontFamily: 'Outfit, sans-serif', borderColor: '#E2EBE9' }}>
        {numbered && <span className="text-sm font-bold px-2 py-0.5 rounded text-white shrink-0" style={{ backgroundColor: '#194449' }}>{numbered}</span>}
        {title}
      </h2>
    )}
    {children}
  </div>
);

export const P = ({ children }) => (
  <p className="text-gray-600 leading-relaxed mb-4 text-sm">{children}</p>
);

export const UL = ({ items }) => (
  <ul className="space-y-2 text-gray-600 text-sm mb-4 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: '#194449' }}></span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function TrustLayout({ title, lastUpdated, versionHistory, sections, children, testId }) {
  const { pathname } = useLocation();

  const handlePrint = () => {
    const prev = document.title;
    document.title = `Technofocus — ${title}`;
    window.print();
    setTimeout(() => { document.title = prev; }, 800);
  };

  return (
    <div data-testid={testId}>
      {/* Print-only header */}
      <div className="hidden print:block print:mb-8 print:px-8">
        <p className="text-xs text-gray-500 mb-1">Technofocus Pte. Ltd. · privacy@technofocus.ai</p>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {lastUpdated && <p className="text-xs text-gray-500 mt-1">Last Updated: {lastUpdated}</p>}
        <hr className="mt-4 border-gray-300" />
      </div>

      {/* Hero */}
      <section
        className="pt-32 pb-14 print:hidden"
        style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-sm mb-5" style={{ color: '#B9DCD2' }}>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/trust" className="hover:text-white transition-colors">Trust Center</Link>
            {pathname !== '/trust' && (
              <>
                <span>/</span>
                <span className="text-white">{title}</span>
              </>
            )}
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield size={15} style={{ color: '#D4EC8E' }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#D4EC8E' }}>Trust & Privacy</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-light text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
              {lastUpdated && (
                <p className="text-sm mt-2.5" style={{ color: 'rgba(185,220,210,0.8)' }}>Last Updated: {lastUpdated}</p>
              )}
            </div>
            <button
              onClick={handlePrint}
              data-testid="pdf-download-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all shrink-0 border"
              style={{ borderColor: 'rgba(212,236,142,0.5)', color: '#D4EC8E', backgroundColor: 'rgba(212,236,142,0.1)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(212,236,142,0.2)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(212,236,142,0.1)'}
            >
              <Download size={14} /> Download PDF
            </button>
          </div>
        </div>
      </section>

      {/* Mobile trust nav */}
      <div className="lg:hidden print:hidden overflow-x-auto border-b bg-white" style={{ borderColor: '#E2EBE9' }}>
        <div className="flex gap-1 px-4 py-3 w-max">
          {trustNav.map(page => (
            <Link
              key={page.href}
              to={page.href}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                pathname === page.href ? 'text-white' : 'text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200'
              }`}
              style={pathname === page.href ? { backgroundColor: '#194449' } : {}}
            >
              {page.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ backgroundColor: '#FFF8F3' }} className="print:bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 print:py-0 print:px-0">
          <div className="flex gap-8 items-start">

            {/* Sticky sidebar */}
            <aside className="hidden lg:block w-64 shrink-0 print:hidden">
              <div className="sticky top-28 space-y-4">

                {/* Trust navigation */}
                <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#E2EBE9' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3 px-1" style={{ color: '#194449' }}>Trust & Privacy</p>
                  <nav className="space-y-0.5">
                    {trustNav.map(page => {
                      const Icon = page.icon;
                      const active = pathname === page.href;
                      return (
                        <Link
                          key={page.href}
                          to={page.href}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                            active ? 'font-semibold' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                          style={active ? { backgroundColor: '#E5F3F0', color: '#194449' } : {}}
                        >
                          <Icon size={14} className="shrink-0" style={active ? { color: '#194449' } : { color: '#9CA3AF' }} />
                          <span className="leading-tight">{page.label}</span>
                        </Link>
                      );
                    })}
                  </nav>
                </div>

                {/* In-page sections */}
                {sections && sections.length > 0 && (
                  <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#E2EBE9' }}>
                    <p className="text-xs font-bold uppercase tracking-widest mb-3 px-1" style={{ color: '#6B7280' }}>On This Page</p>
                    <nav className="space-y-0.5">
                      {sections.map(s => (
                        <button
                          key={s.id}
                          onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                          className="w-full text-left block px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors"
                        >
                          {s.label}
                        </button>
                      ))}
                    </nav>
                  </div>
                )}

                {/* PDF button */}
                <button
                  onClick={handlePrint}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
                  style={{ backgroundColor: '#194449' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}
                >
                  <Download size={14} /> Download PDF
                </button>

                {/* Contact card */}
                <div className="rounded-2xl border p-4 text-center" style={{ borderColor: '#E2EBE9', backgroundColor: '#E5F3F0' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#194449' }}>Data Protection Queries</p>
                  <a href="mailto:privacy@technofocus.ai" className="text-xs hover:underline flex items-center justify-center gap-1" style={{ color: '#194449' }}>
                    privacy@technofocus.ai <ExternalLink size={10} />
                  </a>
                </div>

              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border p-7 md:p-10 print:border-0 print:p-0 print:rounded-none" style={{ borderColor: '#E2EBE9' }}>
                {versionHistory && <VersionHistory history={versionHistory} />}
                {children}
              </div>
            </main>

          </div>
        </div>
      </div>
    </div>
  );
}
